const getApiBaseUrl = (): string => {
  const isWebProduction = typeof window !== 'undefined' && 
    window.location.hostname !== 'localhost' && 
    window.location.hostname !== '127.0.0.1';

  if (isWebProduction) {
    const rawUrl = process.env.REACT_APP_API_URL || '';
    if (!rawUrl || rawUrl.includes('localhost') || rawUrl.includes('127.0.0.1')) {
      return 'https://api.multigym.com.ar/api';
    }
    return rawUrl;
  }

  return process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
};

export const API_BASE_URL = getApiBaseUrl();

export const isMySqlEnabled = (): boolean => {
  return process.env.REACT_APP_USE_MYSQL !== 'false';
};


export const getImageUrl = (pathOrUrl?: string | null): string => {
  if (!pathOrUrl) return '';

  const serverOrigin = API_BASE_URL.replace(/\/api\/?$/, '');
  let cleanPath = pathOrUrl.trim();

  // Si corremos en producción web y la URL trae localhost o IP local, limpiamos la url base
  if (
    typeof window !== 'undefined' &&
    window.location.hostname !== 'localhost' &&
    window.location.hostname !== '127.0.0.1'
  ) {
    if (cleanPath.includes('localhost') || cleanPath.includes('127.0.0.1') || cleanPath.includes('192.168.')) {
      cleanPath = cleanPath.replace(/^https?:\/\/[^\/]+/, '');
    }
  }

  // 1. Si contiene /uploads/, extraer la ruta relativa limpia y anteponer serverOrigin activo
  if (cleanPath.includes('/uploads/')) {
    const relativePath = cleanPath.substring(cleanPath.indexOf('/uploads/'));
    return `${serverOrigin}${relativePath}`;
  }

  // 2. URLs externas / Data URLs / Cloudinary / Blob
  if (
    cleanPath.startsWith('http://') ||
    cleanPath.startsWith('https://') ||
    cleanPath.startsWith('data:') ||
    cleanPath.startsWith('blob:')
  ) {
    return cleanPath;
  }

  return `${serverOrigin}${cleanPath.startsWith('/') ? '' : '/'}${cleanPath}`;
};


const getHeaders = (isFormData: boolean = false): HeadersInit => {
  const headers: Record<string, string> = {};

  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }

  const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
};

export const apiClient = {
  async get<T>(endpoint: string, params?: Record<string, string>): Promise<T> {
    const url = new URL(`${API_BASE_URL}${endpoint}`);
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          url.searchParams.append(key, params[key]);
        }
      });
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: getHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
    }

    return response.json();
  },

  async post<T>(endpoint: string, body: any): Promise<T> {
    const isFormData = body instanceof FormData;
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: getHeaders(isFormData),
      body: isFormData ? body : JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
    }

    return response.json();
  },

  async put<T>(endpoint: string, body: any): Promise<T> {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
    }

    return response.json();
  },

  async delete<T>(endpoint: string): Promise<T> {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
    }

    return response.json();
  },

  async uploadFile(file: File): Promise<{ url: string; filename: string }> {
    const formData = new FormData();
    formData.append('image', file);

    const response = await fetch(`${API_BASE_URL}/upload`, {
      method: 'POST',
      headers: getHeaders(true),
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Error subiendo archivo');
    }

    return response.json();
  },
};


export default apiClient;
